import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Tooltip, theme, Empty, Modal, message, Pagination } from 'antd';
import ReportingChartSkeleton from './ReportingChartSkeleton';
import {
    IconChartPie, IconTable, IconDownload, IconExportPng, IconExpand, IconMaximize, IconCompress, IconStat, IconCopy,
} from './reportingIcons';
import StatCardView from './StatCardView';
import { buildStatViewModel, isStatVizType } from './reportingStatCard';
import ReactECharts from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart, LineChart, ScatterChart, RadarChart, TreemapChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useTranslation } from 'react-i18next';
import { downloadCsvFromRows, tabSeparatedTextFromRows } from '../../../utils/exportUtils';
import { filterVisibleColumns } from './dashboardPortability';
import { formatReportingDisplayValue } from './reportingChartUtils';
import { DEFAULT_CHART_PALETTE, colorsForRows } from './reportingCategoryColors';
import {
    rollupPieRows,
    sortRowsByPrimaryValue,
    buildCategoryDataZoom,
} from './reportingChartDataPrep';
import { isTimeSeriesChart } from './chartVizTypes';
import { isHierarchicalDataset } from './reportingDatasetShape';
import HierarchicalTreemap from './HierarchicalTreemap';

echarts.use([
    TitleComponent, TooltipComponent, GridComponent, LegendComponent, DataZoomComponent,
    PieChart, BarChart, LineChart, ScatterChart, RadarChart, TreemapChart, CanvasRenderer,
]);

const CARTESIAN_TYPES = ['bar', 'line', 'bar_horizontal', 'stacked_bar', 'area'];
const BAR_CHART_TYPES = new Set(['bar', 'bar_horizontal', 'stacked_bar']);
const STACKED_TYPES = new Set(['stacked_bar']);

const CARTESIAN_GRID = { left: 12, right: '4%', bottom: 24, top: 8, containLabel: true };

function categoryAxisLabel(categories) {
    const count = categories.length;
    const maxLen = categories.reduce((max, c) => Math.max(max, String(c ?? '').length), 0);
    return {
        rotate: count > 8 ? 30 : 0,
        fontSize: 10,
        hideOverlap: true,
        ...(maxLen > 24 ? { width: 72, overflow: 'truncate', ellipsis: '...' } : {}),
    };
}

function chartLegendTextStyle(token, { expanded = false, many = false } = {}) {
    return {
        color: token.colorTextSecondary,
        fontSize: expanded ? 12 : 10,
        ...(many ? { width: expanded ? 140 : 88, overflow: 'truncate' } : {}),
    };
}

function chartLegendPageStyle(token) {
    return {
        pageTextStyle: { color: token.colorTextSecondary },
        pageIconColor: token.colorTextQuaternary,
        pageIconInactiveColor: token.colorTextQuaternary,
    };
}

function chartLegend(token, config = {}) {
    return {
        textStyle: chartLegendTextStyle(token, config),
        ...chartLegendPageStyle(token),
    };
}

const EXPAND_MODAL_Z_INDEX = 2000;

const EXPAND_MODAL_BODY_STYLES = {
    body: {
        paddingTop: 8,
        paddingBottom: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: 'min(75vh, 740px)',
    },
};

const FULLSCREEN_EXPAND_MODAL_STYLES = {
    content: {
        height: '100vh',
        maxHeight: '100vh',
        width: '100vw',
        maxWidth: '100vw',
        margin: 0,
        top: 0,
        paddingBottom: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    body: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 16px 16px',
        minHeight: 0,
    },
    header: {
        flexShrink: 0,
        marginBottom: 0,
    },
};

/** Convert a ReportResultDto dataset + vizSpec to an ECharts option. */
export function buildOption(dataset, vizSpec, token, { expanded = false } = {}) {
    const { columns = [], rows = [] } = dataset;
    const { type = 'bar', encoding = {}, colors = [], colorScheme } = vizSpec;
    const palette = Array.isArray(colors) && colors.length > 0 ? colors : DEFAULT_CHART_PALETTE;
    const { category, value } = encoding;

    const catCol = category
        || columns.find(c => c.key === 'category')?.key
        || columns.find(c => c.key === 'period')?.key
        || columns.find(c => c.type === 'category' || c.type === 'text' || c.type === 'datetime')?.key;
    const valCols = value
        ? (Array.isArray(value) ? value : [value])
        : columns.filter(c => c.type === 'number').map(c => c.key);

    if (!catCol || valCols.length === 0) return null;

    const catColumn = columns.find(c => c.key === catCol);
    const formatCat = (r) => {
        const v = r[catCol];
        if (v == null) return '';
        return String(formatReportingDisplayValue(v, catColumn?.type));
    };

    const valKey = valCols[0];
    const categories = rows.map(formatCat);

    if (type === 'treemap' && isHierarchicalDataset(columns)) {
        return null;
    }

    if (type === 'pie' || type === 'donut' || type === 'treemap') {
        const pieRows = rollupPieRows(rows, catCol, valKey);
        const sliceColors = colorsForRows(pieRows, catCol, colorScheme);
        const data = pieRows.map((r, i) => ({
            name: formatCat(r),
            value: Number(r[valKey]) || 0,
            itemStyle: { color: sliceColors[i] },
        }));

        if (type === 'treemap') {
            return {
                color: palette,
                tooltip: {
                    trigger: 'item',
                    formatter: (p) => {
                        const total = data.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                        const value = Number(p.value) || 0;
                        const share = total > 0 ? ` (${((value / total) * 100).toFixed(1)}%)` : '';
                        return `<strong>${p.name}</strong><br/>${value}${share}`;
                    },
                },
                series: [{
                    type: 'treemap',
                    roam: false,
                    nodeClick: false,
                    breadcrumb: { show: false },
                    left: 0,
                    right: 0,
                    top: expanded ? 6 : 4,
                    bottom: expanded ? 6 : 4,
                    label: {
                        show: true,
                        fontSize: expanded ? 12 : 10,
                        overflow: 'truncate',
                        ellipsis: '…',
                    },
                    upperLabel: { show: false },
                    itemStyle: {
                        borderColor: token.colorBgContainer,
                        borderWidth: 0,
                        gapWidth: 0,
                    },
                    emphasis: {
                        itemStyle: {
                            borderColor: token.colorPrimary,
                            borderWidth: 2,
                        },
                    },
                    data,
                }],
            };
        }

        const many = data.length > 6;
        return {
            color: palette,
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: {
                type: many ? 'scroll' : 'plain',
                orient: 'vertical',
                right: expanded ? 16 : 4,
                top: 'middle',
                height: many ? (expanded ? '85%' : '75%') : undefined,
                ...chartLegend(token, { expanded, many }),
                pageIconSize: expanded ? 10 : 8,
            },
            series: [{
                type: 'pie',
                radius: type === 'donut'
                    ? (expanded ? ['42%', '68%'] : ['36%', '58%'])
                    : (expanded ? '62%' : '52%'),
                center: expanded ? ['38%', '50%'] : ['32%', '50%'],
                label: { show: false },
                data,
            }],
        };
    }

    if (type === 'scatter') {
        const yKey = valCols[0];
        const focusLatest = isTimeSeriesChart(type, catCol, catColumn);
        const { dataZoom, gridExtra } = buildCategoryDataZoom({
            categories,
            expanded,
            focusLatest,
        });
        return {
            color: palette,
            tooltip: {
                trigger: 'item',
                formatter: (p) => {
                    const idx = p.value?.[0];
                    const label = categories[idx] ?? '';
                    const y = p.value?.[1] ?? 0;
                    return `<strong>${label}</strong><br/>${y}`;
                },
            },
            grid: { ...CARTESIAN_GRID, ...gridExtra },
            ...(dataZoom ? { dataZoom } : {}),
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: categoryAxisLabel(categories),
            },
            yAxis: { type: 'value' },
            series: [{
                type: 'scatter',
                symbolSize: 10,
                data: rows.map((r, i) => [i, Number(r[yKey]) || 0]),
            }],
        };
    }

    if (type === 'radar' && valCols.length >= 1) {
        const maxVal = Math.max(...rows.map(r => Number(r[valKey]) || 0), 1);
        return {
            color: palette,
            tooltip: {},
            radar: {
                indicator: rows.map(r => ({
                    name: formatReportingDisplayValue(r[catCol], catColumn?.type),
                    max: maxVal * 1.1,
                })),
            },
            series: [{
                type: 'radar',
                data: [{ value: rows.map(r => Number(r[valKey]) || 0) }],
            }],
        };
    }

    if (CARTESIAN_TYPES.includes(type)) {
        const isHorizontal = type === 'bar_horizontal';
        const isLine = type === 'line' || type === 'area';
        const isBarChart = BAR_CHART_TYPES.has(type);
        const seriesType = isLine ? 'line' : 'bar';
        const displayRows = isBarChart ? sortRowsByPrimaryValue(rows, valKey) : rows;
        const displayCategories = displayRows.map(formatCat);
        const barColors = colorsForRows(displayRows, catCol, colorScheme);
        const focusLatest = !isBarChart && isTimeSeriesChart(type, catCol, catColumn);
        const { dataZoom, gridExtra } = buildCategoryDataZoom({
            categories: displayCategories,
            horizontal: isHorizontal,
            expanded,
            focusLatest,
        });
        const seriesList = valCols.map(k => ({
            name: columns.find(c => c.key === k)?.label || k,
            type: seriesType,
            stack: STACKED_TYPES.has(type) ? 'total' : undefined,
            areaStyle: type === 'area' ? {} : undefined,
            data: displayRows.map(r => Number(r[k]) || 0),
            ...(isHorizontal && !isLine && valCols.length === 1
                ? {
                    itemStyle: {
                        color: (params) => barColors[params.dataIndex] ?? palette[params.dataIndex % palette.length],
                    },
                }
                : {}),
        }));
        if (isHorizontal) {
            return {
                color: palette,
                tooltip: { trigger: 'axis' },
                legend: valCols.length > 1 ? { bottom: 0, ...chartLegend(token) } : { show: false },
                grid: { ...CARTESIAN_GRID, ...gridExtra },
                ...(dataZoom ? { dataZoom } : {}),
                xAxis: { type: 'value' },
                yAxis: {
                    type: 'category',
                    data: displayCategories,
                    inverse: true,
                    axisLabel: { fontSize: 10, width: 72, overflow: 'truncate' },
                },
                series: seriesList,
            };
        }
        return {
            color: palette,
            tooltip: { trigger: 'axis' },
            legend: valCols.length > 1 ? { bottom: 0, ...chartLegend(token) } : { show: false },
            grid: { ...CARTESIAN_GRID, ...gridExtra },
            ...(dataZoom ? { dataZoom } : {}),
            xAxis: {
                type: 'category',
                data: displayCategories,
                axisLabel: categoryAxisLabel(displayCategories),
            },
            yAxis: { type: 'value' },
            series: seriesList,
        };
    }

    return null;
}

function initialDisplayMode(vizSpec) {
    if (vizSpec?.mode === 'table' || vizSpec?.mode === 'chart') return vizSpec.mode;
    if (vizSpec?.type === 'table') return 'table';
    if (isStatVizType(vizSpec)) return 'chart';
    return 'chart';
}

/** Chart / table / export controls — intended for Card extra (top right). */
export function ChartToolbar({
    mode,
    onModeChange,
    onExportCsv,
    onExportPng,
    onCopyTable,
    onExpand,
    onToggleMaximize,
    expandMaximized = false,
    showPngExport = true,
    showExpand = true,
    showMaximize = false,
    disabled = false,
    vizSpec,
}) {
    const { t } = useTranslation();
    const isStat = isStatVizType(vizSpec);

    return (
        <Space size={4} onClick={e => e.stopPropagation()}>
            {showExpand && onExpand && (
                <Tooltip title={t('Expand')}>
                    <Button
                        size="small"
                        icon={<IconExpand />}
                        disabled={disabled}
                        onClick={onExpand}
                    />
                </Tooltip>
            )}
            {showMaximize && onToggleMaximize && !expandMaximized && (
                <Tooltip title={t('Maximize')}>
                    <Button
                        size="small"
                        icon={<IconMaximize />}
                        disabled={disabled}
                        onClick={onToggleMaximize}
                    />
                </Tooltip>
            )}
            {showMaximize && onToggleMaximize && expandMaximized && (
                <Tooltip title={t('Exit full screen')}>
                    <Button
                        size="small"
                        icon={<IconCompress />}
                        disabled={disabled}
                        onClick={onToggleMaximize}
                    />
                </Tooltip>
            )}
            <Tooltip title={isStat ? t('Counts') : t('Chart')}>
                <Button
                    size="small"
                    type={mode === 'chart' ? 'primary' : 'default'}
                    icon={isStat ? <IconStat /> : <IconChartPie />}
                    disabled={disabled}
                    onClick={() => onModeChange('chart')}
                />
            </Tooltip>
            <Tooltip title={t('Table')}>
                <Button
                    size="small"
                    type={mode === 'table' ? 'primary' : 'default'}
                    icon={<IconTable />}
                    disabled={disabled}
                    onClick={() => onModeChange('table')}
                />
            </Tooltip>
            {mode === 'table' && onCopyTable && (
                <Tooltip title={t('Copy to clipboard')}>
                    <Button size="small" icon={<IconCopy />} disabled={disabled} onClick={onCopyTable} />
                </Tooltip>
            )}
            <Tooltip title={t('Export CSV')}>
                <Button size="small" icon={<IconDownload />} disabled={disabled} onClick={onExportCsv} />
            </Tooltip>
            {showPngExport && mode === 'chart' && (
                <Tooltip title={t('Export PNG')}>
                    <Button size="small" icon={<IconExportPng />} disabled={disabled} onClick={onExportPng} />
                </Tooltip>
            )}
        </Space>
    );
}

const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function ChartTableView({ columns, rows, defaultPageSize = 20 }) {
    const [pagination, setPagination] = useState({ current: 1, pageSize: defaultPageSize });
    const bodyRef = useRef(null);
    const scrollYRef = useRef(undefined);
    const [scrollY, setScrollY] = useState(undefined);

    const pagedRows = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return rows.slice(start, start + pagination.pageSize);
    }, [rows, pagination]);

    useEffect(() => {
        if (pagination.current > 1 && pagedRows.length === 0 && rows.length > 0) {
            setPagination(prev => ({ ...prev, current: 1 }));
        }
    }, [pagination.current, pagedRows.length, rows.length]);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return undefined;

        const updateScroll = () => {
            requestAnimationFrame(() => {
                if (!bodyRef.current) return;
                const thead = bodyRef.current.querySelector('.ant-table-thead');
                const reserve = (thead?.offsetHeight ?? 40) + 2;
                const next = Math.max(80, bodyRef.current.clientHeight - reserve);
                if (scrollYRef.current != null && Math.abs(next - scrollYRef.current) < 2) return;
                scrollYRef.current = next;
                setScrollY(next);
            });
        };

        updateScroll();
        const ro = new ResizeObserver(updateScroll);
        ro.observe(el);
        return () => ro.disconnect();
    }, [pagination.pageSize]);

    return (
        <div
            style={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden',
                userSelect: 'text',
                display: 'flex',
                flexDirection: 'column',
            }}
            onMouseDown={e => e.stopPropagation()}
        >
            <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <Table
                    size="small"
                    columns={columns}
                    dataSource={pagedRows.map((r, i) => ({ ...r, _key: i }))}
                    rowKey="_key"
                    scroll={{ x: true, y: scrollY }}
                    pagination={false}
                />
            </div>
            {rows.length > 0 && (
                <Pagination
                    size="small"
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={rows.length}
                    showSizeChanger
                    pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
                    onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                    style={{ flexShrink: 0, marginTop: 8 }}
                />
            )}
        </div>
    );
}

function ChartViewContent({
    loading,
    error,
    dataset,
    mode,
    option,
    vizSpec,
    visibleColumns,
    chartRef,
    remountKey,
    chartStyle,
    t,
    token,
    tablePageSize = 20,
    expanded = false,
}) {
    if (loading) {
        return <ReportingChartSkeleton vizSpec={vizSpec} mode={mode} />;
    }
    if (error) {
        return <div style={{ color: token.colorError, padding: 4, fontSize: 12 }}>{error}</div>;
    }
    if (!dataset) {
        return <Empty description={t('No data')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 8 }} />;
    }

    const tableColumns = visibleColumns.map(c => ({
        title: c.label || c.key,
        dataIndex: c.key,
        key: c.key,
        ellipsis: true,
        render: (text) => formatReportingDisplayValue(text, c.type),
    }));

    if (mode === 'chart' && isStatVizType(vizSpec)) {
        if (!buildStatViewModel(dataset, vizSpec)) {
            return (
                <Empty
                    description={t('Pick a volume column for counts, or switch to Table view')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 8 }}
                />
            );
        }
        return (
            <StatCardView
                dataset={dataset}
                vizSpec={vizSpec}
                expanded={expanded}
            />
        );
    }

    if (mode === 'chart') {
        const isHierarchyTreemap = vizSpec?.type === 'treemap'
            && isHierarchicalDataset(dataset?.columns);
        if (isHierarchyTreemap) {
            return (
                <HierarchicalTreemap
                    dataset={dataset}
                    vizSpec={vizSpec}
                    chartRef={chartRef}
                    remountKey={remountKey}
                    style={chartStyle}
                    expanded={expanded}
                />
            );
        }
        if (!option) {
            return (
                <Empty
                    description={t('Pick category and value columns above, or switch to Table view')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 8 }}
                />
            );
        }
        return (
            <div style={{ flex: 1, minHeight: 0, height: '100%', width: '100%', overflow: 'hidden' }}>
                <ReactECharts
                    key={`${remountKey}-${mode}-${dataset?.rows?.length ?? 0}`}
                    ref={chartRef}
                    echarts={echarts}
                    option={option}
                    style={{ width: '100%', height: '100%', minHeight: 0 }}
                    notMerge
                    lazyUpdate
                />
            </div>
        );
    }

    return (
        <ChartTableView
            key={remountKey}
            columns={tableColumns}
            rows={dataset.rows}
            defaultPageSize={tablePageSize}
        />
    );
}

/** Chart view state + toolbar + body (use toolbar in Card extra via toolbarInHeader). */
export function useChartView({
    dataset,
    vizSpec,
    title,
    loading = false,
    error = null,
    defaultMode,
    remountKey = '',
    showExpand = true,
}) {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const chartRef = useRef(null);
    const expandChartRef = useRef(null);
    const [mode, setMode] = useState(defaultMode ?? initialDisplayMode(vizSpec));
    const [expandOpen, setExpandOpen] = useState(false);
    const [expandMaximized, setExpandMaximized] = useState(false);

    const closeExpandModal = useCallback(() => {
        setExpandOpen(false);
        setExpandMaximized(false);
    }, []);

    const toggleExpandMaximize = useCallback(() => {
        setExpandMaximized(prev => !prev);
    }, []);

    useEffect(() => {
        setMode(defaultMode ?? initialDisplayMode(vizSpec));
    }, [defaultMode, vizSpec?.mode, vizSpec?.type]);

    const isStat = isStatVizType(vizSpec);

    const option = useMemo(() => {
        if (!dataset || mode === 'table' || isStat) return null;
        return buildOption(dataset, vizSpec ?? {}, token);
    }, [dataset, vizSpec, mode, token, isStat]);

    const expandedOption = useMemo(() => {
        if (!dataset || mode === 'table' || isStat) return null;
        return buildOption(dataset, vizSpec ?? {}, token, { expanded: true });
    }, [dataset, vizSpec, mode, token, isStat]);

    const resizeChart = useCallback((ref) => {
        ref?.current?.getEchartsInstance?.()?.resize();
    }, []);

    useEffect(() => {
        const onResize = () => resizeChart(chartRef);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [resizeChart]);

    useEffect(() => {
        if (expandOpen && !isStat) {
            const timer = setTimeout(() => resizeChart(expandChartRef), 150);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [expandOpen, expandMaximized, mode, option, resizeChart, isStat]);

    const visibleColumns = useMemo(
        () => filterVisibleColumns(dataset?.columns ?? []),
        [dataset?.columns],
    );

    const exportCsv = useCallback(() => {
        if (!dataset) return;
        const columns = visibleColumns.map(c => ({ label: c.label || c.key, getValue: r => r[c.key] }));
        downloadCsvFromRows(dataset.rows, columns, `${title || 'export'}.csv`);
    }, [dataset, title, visibleColumns]);

    const copyTable = useCallback(async () => {
        if (!dataset?.rows?.length) return;
        const text = tabSeparatedTextFromRows(
            dataset.rows,
            visibleColumns,
            (value, type) => formatReportingDisplayValue(value, type),
        );
        try {
            await navigator.clipboard.writeText(text);
            message.success(t('Copied to clipboard'));
        } catch {
            message.error(t('Failed to copy to clipboard'));
        }
    }, [dataset, visibleColumns, t]);

    const exportPng = useCallback(() => {
        const activeRef = expandOpen ? expandChartRef : chartRef;
        const url = activeRef.current?.getEchartsInstance?.()?.getDataURL({
            pixelRatio: 2,
            backgroundColor: token.colorBgContainer,
        });
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title || 'chart'}.png`;
        a.click();
    }, [title, token, expandOpen]);

    const toolbarDisabled = loading || !dataset;

    const toolbarProps = {
        mode,
        onModeChange: setMode,
        onExportCsv: exportCsv,
        onExportPng: exportPng,
        onCopyTable: copyTable,
        onExpand: showExpand && dataset ? () => setExpandOpen(true) : undefined,
        showExpand,
        showPngExport: mode === 'chart' && !isStat,
        disabled: toolbarDisabled,
        vizSpec,
    };

    const toolbar = <ChartToolbar {...toolbarProps} />;

    const body = (
        <ChartViewContent
            loading={loading}
            error={error}
            dataset={dataset}
            mode={mode}
            option={option}
            vizSpec={vizSpec}
            visibleColumns={visibleColumns}
            chartRef={chartRef}
            remountKey={remountKey}
            chartStyle={{ flex: 1, minHeight: 0, height: '100%', width: '100%' }}
            t={t}
            token={token}
        />
    );

    const expandModalToolbarProps = {
        ...toolbarProps,
        showExpand: false,
        showMaximize: true,
        onToggleMaximize: toggleExpandMaximize,
        expandMaximized,
    };

    const expandModal = showExpand ? (
        <Modal
            title={title || t('Chart')}
            open={expandOpen}
            onCancel={closeExpandModal}
            footer={null}
            width={expandMaximized ? '100%' : 'min(1200px, 92vw)'}
            centered={!expandMaximized}
            destroyOnClose
            zIndex={EXPAND_MODAL_Z_INDEX}
            style={expandMaximized ? { top: 0, padding: 0, margin: 0, maxWidth: '100vw' } : undefined}
            styles={expandMaximized ? FULLSCREEN_EXPAND_MODAL_STYLES : EXPAND_MODAL_BODY_STYLES}
            afterOpenChange={(open) => {
                if (!open) setExpandMaximized(false);
                if (open && !isStat) setTimeout(() => resizeChart(expandChartRef), 200);
            }}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
            }}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, flexShrink: 0 }}>
                    <ChartToolbar {...expandModalToolbarProps} />
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <ChartViewContent
                        loading={loading}
                        error={error}
                        dataset={dataset}
                        mode={mode}
                        option={expandedOption ?? option}
                        vizSpec={vizSpec}
                        visibleColumns={visibleColumns}
                        chartRef={expandChartRef}
                        remountKey={`${remountKey}-expand`}
                        chartStyle={{
                            width: '100%',
                            height: '100%',
                            minHeight: expandMaximized ? 320 : 360,
                        }}
                        t={t}
                        token={token}
                        tablePageSize={50}
                        expanded
                    />
                </div>
            </div>
        </Modal>
    ) : null;

    return { toolbar, body, expandModal, expandOpen, mode, setMode };
}

export default function ChartRenderer({ toolbarInHeader = false, showExpand = false, ...props }) {
    const { toolbar, body } = useChartView({ ...props, showExpand });
    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!toolbarInHeader && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '2px 4px 0', flexShrink: 0 }}>
                    {toolbar}
                </div>
            )}
            {body}
        </div>
    );
}
